import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { LANGUAGE_RESOURCES, SUPPORTED_LANGUAGES, isSupportedLanguage } from './supportedLanguages';
import { setDateRegion } from '../utils/dateFormat';
import { setNumberRegion } from '../utils/numberFormat';

const resources = Object.fromEntries(
  Object.entries(LANGUAGE_RESOURCES).map(([code, meta]) => [code, { translation: meta.translation }])
);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

// Dates and numbers must follow the language that is actually on screen.
// Binding them to i18n itself — rather than to a component that re-renders on
// a Redux update — is what keeps them in step: the language can change before
// the store does (the profile write is async, and may fail), and any component
// that formats a date in that window would otherwise render it in the previous
// language while its labels are already in the new one.
// The country half of the locale is supplied separately by Layout from the
// tenant config; passing only the language here deliberately preserves it.
const syncRegionFormatters = (lng) => {
  setDateRegion(lng);
  setNumberRegion(lng);
};

syncRegionFormatters(i18n.language);
i18n.on('languageChanged', syncRegionFormatters);

export default i18n;

/**
 * Resolve the active language based on priority:
 * 1. User preferred language (post-login)
 * 2. Tenant default language
 * 3. Detected browser/localStorage language
 * 4. 'en' fallback
 */
export function resolveLanguage(userPreferredLanguage, tenantDefaultLanguage, detected) {
  if (isSupportedLanguage(userPreferredLanguage)) return userPreferredLanguage;
  if (isSupportedLanguage(tenantDefaultLanguage)) return tenantDefaultLanguage;
  if (isSupportedLanguage(detected)) return detected;
  return 'en';
}

export function applyLanguage(lang) {
  const resolved = isSupportedLanguage(lang) ? lang : 'en';
  i18n.changeLanguage(resolved);
  localStorage.setItem('i18nextLng', resolved);
  document.documentElement.lang = resolved;
}

/**
 * Returns true when the user has explicitly chosen a language during this
 * session/device — i.e. localStorage was written by applyLanguage. Used by
 * the login screen to decide whether to honour tenant default vs. user choice.
 */
export function hasExplicitLanguageChoice() {
  return !!localStorage.getItem('userLanguageChosen');
}

export function markExplicitLanguageChoice() {
  localStorage.setItem('userLanguageChosen', '1');
}

/**
 * A language chosen while logged out (the login screen's selector) cannot be
 * written to a profile yet. Park it here so the first authenticated load can
 * persist it — otherwise signing in would silently revert the user's pick to
 * whatever their profile still says.
 */
export function setPendingLanguageChoice(lang) {
  if (isSupportedLanguage(lang)) localStorage.setItem('pendingLanguageChoice', lang);
}

/** Read and clear the parked choice — it must only ever be applied once. */
export function takePendingLanguageChoice() {
  const lang = localStorage.getItem('pendingLanguageChoice');
  localStorage.removeItem('pendingLanguageChoice');
  return isSupportedLanguage(lang) ? lang : null;
}
